import { apiClient } from './client';
import { logger } from '@/lib/utils/logger';
import { UserRole } from '../types/authorization';
import { SPECIALTY_CODE_SYSTEM } from './specialties';

/**
 * PractitionerRole data structure
 */
export interface PractitionerRoleData {
  specialty?: string;
  specialtyCode?: string;
  /** Specialties after the first: the code of a catalogue entry, or the free text itself. */
  additionalSpecialties: string[];
  /** The same specialties as display labels. */
  additionalSpecialtyLabels: string[];
  roleAuthorization?: UserRole;
  practitionerRoleId?: string;
}

/**
 * Map a raw PractitionerRole FHIR resource to a PractitionerRoleData object.
 * Shared by fetchPractitionerRole and fetchAllPractitionerRoles.
 */
function mapPractitionerRoleResource(role: any): PractitionerRoleData {
  // The label is stored on the resource itself, so no code→label table is
  // needed here.
  const specialtyCode = role.specialty?.[0]?.coding?.[0]?.code;
  const specialty = role.specialty?.[0]?.coding?.[0]?.display ?? role.specialty?.[0]?.text;
  const extras = (
    (role.specialty ?? []).slice(1) as Array<{
      coding?: Array<{ system?: string; code?: string; display?: string }>;
      text?: string;
    }>
  )
    .map((entry) => {
      const coding = entry.coding?.[0];
      const label = coding?.display ?? entry.text ?? coding?.code ?? '';
      const isCatalogue = coding?.system === SPECIALTY_CODE_SYSTEM && !!coding.code;
      return { value: isCatalogue ? (coding.code as string) : label, label };
    })
    .filter((extra) => extra.value.length > 0);
  const additionalSpecialties = extras.map((extra) => extra.value);
  const additionalSpecialtyLabels = extras.map((extra) => extra.label);
  // The practitioner's role code, e.g. admin, practitioner or frontdesk
  const roleAuthorization = role.code?.[0]?.coding?.[0].code || null;

  return {
    specialty,
    specialtyCode,
    additionalSpecialties,
    additionalSpecialtyLabels,
    roleAuthorization,
    practitionerRoleId: role.id,
  };
}

/**
 * Fetch PractitionerRole resource for a given practitioner
 * Returns specialty, department, and other role information
 */
export async function fetchPractitionerRole(
  practitionerId: string
): Promise<PractitionerRoleData | null> {
  try {
    // Search for PractitionerRole by practitioner reference
    const searchParams = new URLSearchParams();
    searchParams.set('practitioner', `Practitioner/${practitionerId}`);
    searchParams.set('_sort', '-_lastUpdated'); // Get most recent first
    searchParams.set('_count', '10'); // A practitioner can hold several roles

    const response = await apiClient.get<any>(`/PractitionerRole?${searchParams.toString()}`);
    const bundle = response.data;
    const practitionerRoles = (bundle.entry || []).map((entry: any) => entry.resource);

    if (!practitionerRoles || practitionerRoles.length === 0) {
      logger.warn('No PractitionerRole found for practitioner:', practitionerId);
      return null;
    }

    // Most recent role that actually carries a specialty (matches the backend's
    // department lookup); fall back to the most recent role overall.
    const practitionerRole =
      practitionerRoles.find((role: any) => role.specialty?.[0]) ?? practitionerRoles[0];
    logger.debug('Found PractitionerRole:', practitionerRole);

    return mapPractitionerRoleResource(practitionerRole);
  } catch (error) {
    logger.error('Error fetching PractitionerRole:', error);
    return null;
  }
}

/**
 * Fetch all PractitionerRoles and map them by practitioner ID
 * Makes a single API call to fetch all PractitionerRoles, then maps them by practitioner reference
 * Returns a Map of practitionerId -> PractitionerRoleData
 */
export async function fetchAllPractitionerRoles(): Promise<Map<string, PractitionerRoleData>> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('_sort', '-_lastUpdated');
    searchParams.set('_count', '1000'); // Fetch all roles

    const response = await apiClient.get<any>(`/PractitionerRole?${searchParams.toString()}`);
    const bundle = response.data;
    const practitionerRoles = (bundle.entry || []).map((entry: any) => entry.resource);

    const rolesMap = new Map<string, PractitionerRoleData>();

    if (!practitionerRoles || practitionerRoles.length === 0) {
      logger.warn('No PractitionerRoles found');
      return rolesMap;
    }

    // Group by practitioner and keep the most recent one for each
    const practitionerRolesMap = new Map<string, any>();

    for (const role of practitionerRoles) {
      const practitionerRef = role.practitioner?.reference;
      if (!practitionerRef) continue;

      // Extract practitioner ID from reference (e.g., "Practitioner/123" -> "123")
      const practitionerId = practitionerRef.split('/')[1];
      if (!practitionerId) continue;

      // Keep the most recent role for each practitioner (already sorted by _lastUpdated desc)
      if (!practitionerRolesMap.has(practitionerId)) {
        practitionerRolesMap.set(practitionerId, role);
      }
    }

    // Build the final map with extracted data
    for (const [practitionerId, role] of practitionerRolesMap) {
      rolesMap.set(practitionerId, mapPractitionerRoleResource(role));
    }

    logger.debug('Fetched PractitionerRoles for', rolesMap.size, 'practitioners');
    return rolesMap;
  } catch (error) {
    logger.error('Error fetching all PractitionerRoles:', error);
    return new Map();
  }
}
